import React, {useRef} from 'react';
import {graphql} from 'gatsby';
import PropTypes from 'prop-types';

import get from 'lodash/get';

import Container from 'ssg-themes/src/themes/hijunior/components/Container/Container';
import ContentHtml from 'ssg-themes/src/themes/hijunior/modules/ContentHtml/ContentHtml';
import ArticleHero from 'ssg-themes/src/themes/hijunior/modules/ArticleHero/ArticleHero';
import GenericHeading from 'ssg-themes/src/themes/hijunior/modules/GenericHeading/GenericHeading';
import GenericBlog from 'ssg-themes/src/themes/hijunior/modules/GenericBlog/GenericBlog';
import SimilarArticles from 'ssg-themes/src/themes/hijunior/modules/SimilarArticles/SimilarArticles';
import Authorship from 'ssg-themes/src/themes/hijunior/modules/Authorship/Authorship';
import Cta2 from 'ssg-themes/src/themes/hijunior/modules/Cta2/Cta2';
import Mediabar from "ssg-themes/src/themes/hijunior/modules/Mediabar/Mediabar";
import TableOfContents from "ssg-themes/src/themes/hijunior/modules/TableOfContents/TableOfContents";
import {organizationType} from "ssg-themes/src/common/structuredData/organizationType";
import mg from "ssg-themes/src/common/constants/schema/organization/mg";

import {useSiteMetadata} from "../../hooks/useSiteMetadata";

import ScoreApi from '../../components/ScoreApi/ScoreApi';
import Seo from '../../components/Seo/Seo'

import getReadingTime from "../../utils/getReadingTime";
import getBreadcrumbs from "../../utils/getBreadcrumbs";
import parseAuthor from '../../utils/parseAuthor';
import replaceAdsProductIDs from '../../utils/replaceAdsProductIDs';

import {authorFromTypeToTitle} from "../../data/pl/authorFromTypeToTitle";
import {tooltipData} from "../../data/pl/blogPost.data";
import {ICONS} from '../../constants/icons';

import getStrucutredData from "../BlogPost/getStrucutredData";

import './BlogList.scss';

/**
 * Props Type declaration
 */
const propTypes = {
  pageHeader: PropTypes.object
};

/**
 * Blog List Template
 */
const BlogList = props => {
  const siteMetadata = useSiteMetadata();

  const category = get(props, 'data.contentfulBlogList');
  const postsModel = get(props, 'data.allContentfulBlogPost.edges');

  const isPostTemplate = category.templateType === 'Blog Post';

  const Base = props.pageContext.locale === 'pl' ? require('../base-pl').default : require('../base').default
  const locale = category.node_locale === 'en-US' ? '' : `/${category.node_locale}`;

  const siteUrl = `${siteMetadata.siteUrl}${locale}`
  const categorySlug = `/${category.slug}`
  const categoryUrl = `${siteUrl}${categorySlug}`
  const articleSlug = `${categorySlug}`
  const articleUrl = `${siteUrl}${articleSlug}`
  const brandName = siteMetadata.brandName
  const {content, cleanContent, faqList, votes, hasNoindex} = props.pageContext;
  const timeRequired = content ? getReadingTime(content) : null;

  const parsedContent =
  content
    .replaceAll('<table>', '<div class="table"><div class="tableWrapper"><table>')
    .replaceAll('</table>', '</table></div></div>')
    .replace(/(<img[^>]+>(?:<\/img>)?)/g, "$1")
    .replace(/<img/g, '<img loading="lazy"')
    .replace(/(?:{{\s*ad:\s*([^\s}]+)\s*}}|<p>{{\s*ad:\s*([^\s}]+)\s*}}<\/p>)/g, replaceAdsProductIDs.bind(this, props.pageContext.ads));  

  const articleRef = useRef();

  const posts = postsModel.map(({node}) => {
    const picture = isPostTemplate
      ? {
        src: node.thumbnailImage && node.thumbnailImage.url ? node.thumbnailImage.url : false
      }
      : {
        xs: {
          src: node.thumbnailImage && node.thumbnailImage.url ? node.thumbnailImage.url : false,
          width: 350,
          height: 227
        }
      };

    return {
      picture: {
        ...picture
      },
      heading: node?.seo?.title || node?.shortHeading || node?.heading,
      description: node?.shortContent,
      readTime: node?.content?.content && `${getReadingTime(node.content.content)} min`,
      date: isPostTemplate ? node.shortDate : node.fullDate,
      href: `${node.node_locale === 'pl' ? '/' + node.node_locale : ''}/${category.slug}/${node.slug}`,
      comments: node.category?.slug,
    }
  });

  const authorsObject = {
    author: category.author ? parseAuthor(category.author) : false,
    editedBy: category.editedBy ? parseAuthor(category.editedBy) : false,
    reviewdBy: category.reviewedBy ? parseAuthor(category.reviewedBy) : false,
    factChecker: category.factChecker ? parseAuthor(category.factChecker) : false
  }

  const authors = Object.keys(authorsObject).filter(key => authorsObject[key]).map(key => authorsObject[key]);

  const breadcrumbs = getBreadcrumbs(
    [
      {
        label: '❤️',
        href: `${locale === '' ? '/' : locale}`,
        schemaHref: `${siteUrl}`
      },
      {
        label: category.shortHeading || category.heading,
        schemaHref: `${siteUrl}${categorySlug}`
      },
    ]
  )

  const structuredData = getStrucutredData(category, breadcrumbs, categoryUrl, articleUrl, siteUrl, brandName, cleanContent, votes, faqList, timeRequired);

  const mediabarItems = [
    {
      icon: ICONS['uPacjenta'],
      href: 'https://upacjenta.pl/poradnik/domowe-sposoby-na-bol-gardla',
    },
    {
      icon: ICONS['overit'],
      href: 'https://overit.com/blog/100-tips-from-experts-on-retaining-and-recruiting-talented-employees/',
    },
    {
      picture: {
        src: 'https://hijunior.com/images/upjourney.png',
        alt: 'Mediabar - Upjourney'
      },
      href: 'https://upjourney.com/time-management-tips-for-students',
    },
    {
      picture: {
        src: 'https://hijunior.com/images/justsimplymom.png',
        alt: 'Mediabar - Just simply mom'
      },
      href: 'https://justsimplymom.com/strange-early-pregnancy-stories/',
    }
  ];

  return (
    <Base {...(isPostTemplate && {
      ref: articleRef
    })}>
      <Seo
        lang="pl"
        title={category.seo?.title}
        description={category.seo?.description}
        hasNoindex={hasNoindex}
        meta={[
          {
            property: 'entryID',
            content: category.contentful_id
          },
          {
            property: 'article:published_time',
            content: category.metaPublishedTime
          },
          {
            property: 'article:modified_time',
            content: category.metaUpdatedTime
          },
        ]}
        {...(!isPostTemplate && {
          structuredData: {
            breadcrumbs: {
              itemListElement:
                breadcrumbs.map(item => ({
                  name: item.label,
                  item: item.schemaHref
                }))
            },
            webPage: {
              url: categoryUrl,
              name: category?.seo?.name || category.heading,
              lastReviewed: category.metaUpdatedTime,
              reviewedBy: JSON.parse(organizationType(mg))
            }
          }
        })}
        {...(isPostTemplate && structuredData)}
      />
      {!isPostTemplate &&
        <GenericHeading variant="Category" heading={category.heading} description={category?.shortContent}
                        breadcrumbs={breadcrumbs} breadcrumbsIcon={ICONS.lamfFavicon}/>}
      <div className="blogListWrapper">
        {isPostTemplate && <><ArticleHero
          breadcrumbs={breadcrumbs}
          breadcrumbsIcon={ICONS.lamfFavicon}
          heading={category.heading}
          description={category.shortContent}
          backgroundPicture={category.featuredImage?.url}
          author={category.author && parseAuthor(category.author, {title: authorFromTypeToTitle.author})}
          editedBy={category.editedBy && parseAuthor(category.editedBy, {title: authorFromTypeToTitle.editedBy})}
          reviewedBy={category.reviewedBy && parseAuthor(category.reviewedBy, {title: authorFromTypeToTitle.reviewedBy, isVerified: true})}
          factChecker={category.factChecker && parseAuthor(category.factChecker, {title: authorFromTypeToTitle.factCheckedBy})}

          updatedAt={{
            label: `${authorFromTypeToTitle.editedBy}:`,
            date: category.updatedAt,
            datetime: category.updatedAtDatetime
          }}
          editedOn={{
            label: `${authorFromTypeToTitle.editedBy}:`,
            date: category.editedOn,
            datetime: category.editedOnDatetime
          }}
          reviewedOn={{
            label: `${authorFromTypeToTitle.reviewedBy}:`,
            date: category.reviewedOn,
            datetime: category.reviewedOnDatetime
          }}
          factCheckedOn={{
            label: `${authorFromTypeToTitle.factCheckedBy}:`,
            date: category.factCheckedOn,
            datetime: category.factCheckedOnDatetime
          }}
          i18n={tooltipData}
          readingTime={timeRequired && `${timeRequired} min `}
        />
          <Mediabar
            heading='Media o nas:'
            items={mediabarItems}
          />
        </>
        }
        {(category.content?.content || isPostTemplate) &&
          <Container classname='blogList'>
            <div className='blogList_body' ref={articleRef}>
              {content && <ContentHtml html={parsedContent}/>}
              {isPostTemplate && <TableOfContents heading="Spis treści:" ref={articleRef}/>}
            </div>
            {isPostTemplate && <div className='blogList_footer'>
              <ScoreApi {...votes}  />
            </div>}
          </Container>
        }
        {isPostTemplate && <>
          {authors && authors.length > 0 && <Authorship items={authors}/>}
          {isPostTemplate && <Cta2
            heading='Zadbaj o szczęście swojego dziecka'
            description='Wychowuj bez wątpliwości dzięki HiJunior'
            button={{label: 'Zobacz wszystkie artykuły', href: locale}}
          />}
          {!isPostTemplate && posts.length > 0 && <GenericBlog items={posts} variant='LightGray'/>}
          {isPostTemplate && posts.length > 0 && <SimilarArticles heading="Artykuły" items={posts}/>}
        </>}
        {posts.length > 0 && !isPostTemplate && <GenericBlog items={posts} variant='LightGray'/>}
      </div>
    </Base>
  );
};

BlogList.propTypes = propTypes;

export const pageQuery = graphql`
  fragment authorFields on ContentfulDbAuthor {
    role
    firstName
    lastName
    muckrack
    linkedin
    slug
    hasAuthorPage
    description {
      description
    }
    image {
      url
    }
    imageLarge {
      url
    }
    email
    additionalName
    honorificSuffix
    honorificSuffixDescription
    knowsAbout
    hasOccupation
  }

  query BlogListBySlug(
    $slug: String!,
    $locale: String!,
  ) {
    contentfulBlogList(slug: { eq: $slug }) {
      contentful_id
      slug
      shortHeading
      heading
      shortContent
      node_locale
      featuredImage {
        url
      }
      author  {
        ...authorFields
      }
      editedBy {
        ...authorFields
      }
      reviewedBy {
        ...authorFields
      }
      factChecker {
        ...authorFields
      }
      templateType
      content {
        content
      }
      seo {
        title
        description
      }
      createdAt(locale: $locale, formatString: "DD MMMM, YYYY")
      createdAtDatetime: createdAt(locale: $locale, formatString: "YYYY-MM-DD")
      updatedAt(locale: $locale, formatString: "DD MMMM, YYYY")
      updatedAtDatetime: updatedAt(locale: $locale, formatString: "YYYY-MM-DD")
      editedOn(locale: $locale, formatString: "DD MMMM, YYYY")
      editedOnDatetime: editedOn(locale: $locale, formatString: "YYYY-MM-DD")
      reviewedOn(locale: $locale, formatString: "DD MMMM, YYYY")
      reviewedOnDatetime: reviewedOn(locale: $locale, formatString: "YYYY-MM-DD")
      factCheckedOn(locale: $locale, formatString: "DD MMMM, YYYY")
      factCheckedOnDatetime: factCheckedOn(locale: $locale, formatString: "YYYY-MM-DD")
      rawDate: createdAt
      metaPublishedTime: createdAt(formatString: "YYYY-MM-DDTHH:mm:ssZ")
      metaUpdatedTime: updatedAt(formatString: "YYYY-MM-DDTHH:mm:ssZ")
    }
    allContentfulBlogPost(
      filter: {category: {slug: { eq: $slug, ne: null }, node_locale: {eq: $locale}}, options: {nin: "Hide in listing"}}
      sort: {updatedAt: DESC}
    ) {
      edges {
        node {
          node_locale
          slug
          options
          thumbnailImage {
            url
          }
          heading
          shortHeading
          shortContent
          content {
            content
          }
          fullDate: createdAt(locale: $locale, formatString: "DD MMMM, YYYY")
          shortDate: createdAt(locale: $locale, formatString: "MMM DD")
          rawDate: createdAt
          seo {
            title
          }
        }
      }
    }
  }
`;
export default BlogList;
